import type { UpgradeHint, UpgradeLine } from "./pack-upgrade-operation";
export type UpgradeReviewLine = UpgradeLine & { productName: string; variantName: string };
export type UpgradeReviewAcknowledgement = {
	checkoutId: string;
	operationId: string;
	operationIds: string[];
	fingerprint: string;
};
export type UpgradeCartReview = UpgradeReviewAcknowledgement & {
	status: "ready";
	lines: UpgradeReviewLine[];
};
export type UpgradeCartReviewResult = UpgradeCartReview | { status: "unavailable" };
/** Stable exact identity, not a server authorization token. Names and prices are intentionally absent. */
const compare = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
export function reviewFingerprint(lines: readonly UpgradeLine[]): string {
	return JSON.stringify(
		[...lines]
			.sort((a, b) => compare(a.id, b.id))
			.map((l) => [
				l.id,
				l.variantId,
				l.quantity,
				l.metadata
					.filter((m) => m.key.startsWith("cart_upgrade_"))
					.map((m) => [m.key, m.value])
					.sort(([ak, av], [bk, bv]) => compare(ak, bk) || compare(av, bv)),
			]),
	);
}
export function buildUpgradeReview(
	checkoutId: string,
	operationId: string,
	phase: UpgradeHint["phase"],
	lines: readonly UpgradeReviewLine[],
): UpgradeCartReview | null {
	if (phase !== "adding") return null;
	const operationIds = [
		...new Set(
			lines.flatMap((l) => l.metadata.filter((m) => m.key === "cart_upgrade_operation").map((m) => m.value)),
		),
	].sort();
	if (!operationIds.includes(operationId)) return null;
	const ids = new Set(lines.map((line) => line.id));
	if (
		lines.some(
			(line) =>
				line.metadata.some((m) => m.key === "cart_upgrade_operation" && m.value !== operationId) &&
				line.metadata.some((m) => m.key === "cart_upgrade_source_line" && ids.has(m.value)),
		)
	)
		return null;
	return {
		status: "ready",
		checkoutId,
		operationId,
		operationIds,
		fingerprint: reviewFingerprint(lines),
		lines: [...lines],
	};
}
export function acknowledgesUpgradeSnapshot(
	ack: UpgradeReviewAcknowledgement | null,
	checkoutId: string,
	operationId: string,
	lines: readonly UpgradeLine[],
): boolean {
	return Boolean(
		ack &&
		ack.checkoutId === checkoutId &&
		ack.operationIds.includes(operationId) &&
		ack.fingerprint === reviewFingerprint(lines),
	);
}
